/**
 * Account-plane feed need (consumer feed filters, Stage C).
 *
 * Positions and resting orders are the one consumer of market rows that no
 * VIEW declares: `getMarketPrice` / `usePositionPnl` mark every held ticker
 * from the live store regardless of which page is open. With `player-prop`
 * excluded by default, a prop position would sit unmarked whenever no
 * prop-rendering view was mounted. So the account stores themselves declare
 * the need: while any Kalshi position / open order, or any Polymarket open
 * order whose token resolves to a slug, may be a player prop, the class is
 * opted back in under the `account-plane` source.
 *
 * Polymarket identities are opaque (token / conditionId) until the market has
 * been ingested once (the store's identity indexes are filled by ingest and
 * survive resets). An UNRESOLVED live Poly order therefore fails OPEN: it opts
 * `player-prop` back in until it resolves (Danielle R1 DNL-003 — with props
 * excluded by default the market could otherwise never arrive to classify
 * itself: a deadlock closed around an active order). Positions arrive from
 * the relay as conditionIds the same way; the known gap recorded in the
 * handoff is that a never-seen Poly prop POSITION (no resting order) stays
 * unclassified until a prop surface ingests it once.
 */
import { useAccountStore } from './accountStore';
import { usePolyAccountStore } from './polyAccountStore';
import { useMarketStore } from './marketStore';
import { declareFeedNeeds } from './feedNeeds';
import { tickerMayBePlayerProp } from '@/lib/feedFilter/playerPropTicker';
import { DEFAULT_EXCLUDED_CLASSES } from '@/workers/feedFilter';
import { sportFromTicker } from '@/lib/kalshiTicker/sportFilter';
import { normalizeSport } from './marketFrameNormalization';

export const ACCOUNT_PLANE_SOURCE = 'account-plane';

let _unsubscribe: (() => void) | null = null;
let _lastKey: string | null = null;
// Danielle R10 DNL-009: an UNRESOLVED Poly identity resolves when its market
// is ingested — a MARKET-store event, not an account-store one — so the
// needs must recompute on market-store ticks while anything is unresolved
// (throttled: the store ticks up to 30 Hz; the recompute is cheap but not
// free). Once everything resolves the market subscription goes quiet.
// Danielle R11 DNL-009: the throttle is TRAILING-EDGE — a tick that lands
// inside the window schedules ONE recompute for the window's end instead of
// being dropped. A leading-edge drop lost the causal update whenever an
// unrelated market consumed the slot and the resolving market arrived inside
// the window with nothing after it: the opt-in then stayed on indefinitely.
let _hasUnresolved = false;
let _lastMarketRecomputeMs = 0;
let _pendingMarketRecompute: ReturnType<typeof setTimeout> | null = null;
const MARKET_RECOMPUTE_THROTTLE_MS = 2_000;

function recomputeFromMarketTick(): void {
  const now = Date.now();
  const sinceLast = now - _lastMarketRecomputeMs;
  if (sinceLast >= MARKET_RECOMPUTE_THROTTLE_MS) {
    _lastMarketRecomputeMs = now;
    recomputeAccountFeedNeeds();
    return;
  }
  if (_pendingMarketRecompute != null) return; // one trailing recompute already booked
  _pendingMarketRecompute = setTimeout(() => {
    _pendingMarketRecompute = null;
    _lastMarketRecomputeMs = Date.now();
    recomputeAccountFeedNeeds();
  }, MARKET_RECOMPUTE_THROTTLE_MS - sinceLast);
}

/** Pure: the set of account tickers to classify. Exported for tests. */
export function accountTickers(): string[] {
  const acct = useAccountStore.getState();
  const poly = usePolyAccountStore.getState();
  const market = useMarketStore.getState();
  const out = new Set<string>();
  for (const p of acct.positions) if (p.ticker) out.add(p.ticker);
  for (const o of acct.openOrders) if (o.ticker) out.add(o.ticker);
  for (const o of poly.openOrders) {
    const slug = (o.assetId && market.getTickerByTokenId(o.assetId))
      || (o.conditionId && market.getTickerByConditionId(o.conditionId))
      || null;
    out.add(slug ?? UNRESOLVED_POLY_ORDER);
  }
  return [...out];
}

/** Sentinel for a live Poly order whose identity has not been learned yet —
 *  classified as a POSSIBLE prop (fail open). */
export const UNRESOLVED_POLY_ORDER = 'PM-?unresolved';

/** Sports of the account's tickers — the frame's own sport when the row is
 *  in the store, else the ticker parse. Diagnostic only since the two-tier
 *  floor: the account no longer declares sports (see recomputeAccountFeedNeeds). */
export function accountSports(tickers: readonly string[]): Iterable<string> | 'all' {
  const market = useMarketStore.getState();
  const out = new Set<string>();
  for (const t of tickers) {
    if (t === UNRESOLVED_POLY_ORDER) return 'all';
    const s = normalizeSport(market.getMarket(t)?.sport ?? '') ?? sportFromTicker(t);
    if (s == null) return 'all';
    out.add(s);
  }
  return out;
}

export function recomputeAccountFeedNeeds(): void {
  const tickers = accountTickers();
  _hasUnresolved = tickers.includes(UNRESOLVED_POLY_ORDER);
  const wantsProps = tickers.some((t) => t === UNRESOLVED_POLY_ORDER || tickerMayBePlayerProp(t));
  const key = wantsProps ? 'props' : '';
  if (key === _lastKey) return;
  _lastKey = key;
  if (!wantsProps) { declareFeedNeeds(ACCOUNT_PLANE_SOURCE, null); return; }
  declareFeedNeeds(ACCOUNT_PLANE_SOURCE, {
    // A ticker outside the front-page families may be ANY detail-only
    // class (the classifier cannot tell a prop from a game-event leg), so
    // the account opts every default exclusion back in — fail open.
    classes: [...DEFAULT_EXCLUDED_CLASSES],
    // Deliberately NO sports: the account plane must not keep an edge hot
    // for every sport a position sits in — that made every previously
    // visited sport stay "full pipe" (Dan, 2026-09-02). Off-tab marks come
    // from the hc1 floor (every sport at 1 s); the edge carries only the
    // viewed sport. Empty list = this source constrains the sports axis
    // to nothing of its own.
    sports: [],
    // …and no GAME of its own, for the same reason: an omitted axis means
    // every game, which would cancel a game detail's event narrowing. Held
    // positions outside the open event are marked from the core floor, which
    // never carries the games axis.
    games: [],
  });
}

/** Subscribe once per fleet lifetime; idempotent. */
export function startAccountFeedNeeds(): void {
  if (_unsubscribe) return;
  const unsubs = [
    useAccountStore.subscribe(recomputeAccountFeedNeeds),
    usePolyAccountStore.subscribe(recomputeAccountFeedNeeds),
    useMarketStore.subscribe((s, prev) => {
      if (s.version === prev.version || !_hasUnresolved) return;
      recomputeFromMarketTick();
    }),
  ];
  _unsubscribe = () => { for (const u of unsubs) u(); };
  recomputeAccountFeedNeeds();
}

export function stopAccountFeedNeeds(): void {
  _unsubscribe?.();
  _unsubscribe = null;
  _lastKey = null;
  _hasUnresolved = false;
  _lastMarketRecomputeMs = 0;
  if (_pendingMarketRecompute != null) { clearTimeout(_pendingMarketRecompute); _pendingMarketRecompute = null; }
  declareFeedNeeds(ACCOUNT_PLANE_SOURCE, null);
}
